import { Box, Paper, Typography } from '@mui/material';
import { ReactNode } from 'react';
import {
  getRibbonToastSeverityColor,
  isRibbonToastSticky,
  RibbonToastSeverity,
} from './ribbon-toast-severity';
import { RIBBON_TOAST_ACCENT } from './ribbon-toast-style';
import { RibbonToastAutoCloseBar } from './RibbonToastAutoCloseBar';
import { RibbonToastCloseButton } from './RibbonToastCloseButton';
import { RibbonToastPrimaryAction } from './RibbonToastPrimaryAction';

export type RibbonToastViewProps = {
  /** Primary line; omit when using children for fully custom body. */
  title?: string;
  /** Workflow / category label shown above the subject. */
  eyebrow?: string | null;
  /** Optional secondary line under the title. */
  body?: string | null;
  /** Custom body when the toast is not a simple title/body message. */
  children?: ReactNode;
  severity?: RibbonToastSeverity;
  actionLabel?: string | null;
  onAction?: () => void;
  /** When true, the action chip shows an open-in-new icon. */
  opensInNewTab?: boolean;
  /** When set, renders the dismiss control inside the Paper surface. */
  onClose?: () => void;
  /**
   * When set, shows a gray auto-close progress bar along the Paper bottom edge.
   * Duration should match the toast autoClose value.
   */
  autoCloseMs?: number;
};

/**
 * Presentational ribbon toast: MUI Paper surface with a severity accent strip.
 * Actions use the same outlined chip as the notification feed; auto-close
 * severities also make the Paper clickable as a shortcut.
 */
export function RibbonToastView({
  title,
  eyebrow = null,
  body = null,
  children,
  severity = 'info',
  actionLabel = null,
  onAction,
  opensInNewTab = false,
  onClose,
  autoCloseMs,
}: RibbonToastViewProps) {
  const { width, insetX, insetY, radius } = RIBBON_TOAST_ACCENT;
  const showAction = Boolean(actionLabel && onAction);
  const sticky = isRibbonToastSticky(severity);
  const paperClickable = showAction && !sticky;
  const hasTextBlock = Boolean(eyebrow || title || body);

  return (
    <Paper
      elevation={1}
      onClick={paperClickable ? onAction : undefined}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        // Left padding leaves room for the severity ribbon in the gutter.
        pl: 2,
        pr: 0.5,
        py: 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        overflow: 'hidden',
        cursor: paperClickable ? 'pointer' : 'default',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: insetY,
          bottom: insetY,
          left: insetX,
          width,
          borderRadius: `${radius}px`,
          bgcolor: getRibbonToastSeverityColor(severity),
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          minWidth: 0,
          flex: 1,
        }}
      >
        {hasTextBlock ? (
          <>
            {eyebrow ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {eyebrow}
              </Typography>
            ) : null}
            {title ? (
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                {title}
              </Typography>
            ) : null}
            {body ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.25, wordBreak: 'break-word' }}
              >
                {body}
              </Typography>
            ) : null}
          </>
        ) : null}
        {children}
        {showAction ? (
          <Box sx={{ mt: 0.75 }}>
            <RibbonToastPrimaryAction
              label={actionLabel!}
              onAction={onAction!}
              opensInNewTab={opensInNewTab}
            />
          </Box>
        ) : null}
      </Box>
      {onClose ? <RibbonToastCloseButton onClose={onClose} /> : null}
      {autoCloseMs != null ? <RibbonToastAutoCloseBar durationMs={autoCloseMs} /> : null}
    </Paper>
  );
}
